import React from 'react';
import {
  Container, Typography, Box, Card, CardContent, Tabs, Tab, Grid, TextField,
  Button, FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Switch, FormControlLabel, Avatar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper, Chip, IconButton,
  Link, CardActions, Divider, Collapse, Autocomplete
} from '@mui/material';
import {
  Business as BusinessIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Save as SaveIcon, LocalOffer as LocalOfferIcon,
  ShoppingCart as ShoppingCartIcon, Settings as SettingsIcon,
  FileDownload as ExportIcon, Link as LinkIcon, Phone as PhoneIcon,
  Tablet as TabletIcon, Computer as ComputerIcon, Article as ArticleIcon,
  Schedule as ScheduleIcon, Close as CloseIcon, Category as CategoryIcon,
  ViewModule as TemplateIcon, TrendingUp as UsageIcon,
  Visibility as PreviewIcon, Create as CreateIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  PauseCircleOutline as PauseIcon, PlayCircleOutline as ResumeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import OfferPageBuilderSimple from '../../../components/OfferPageBuilderSimple';
import { useBrandsContext } from '../../BrandsContext';

const StyledCard = styled(Card)(({ theme, expired }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  opacity: expired ? 0.7 : 1,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  textTransform: 'capitalize',
  fontWeight: 'bold',
}));

// URL validation helper function
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (error) {
    return false;
  }
};


const BrandListTab = () => {
  const {
    user, navigate,
    activeTab, setActiveTab,
    brandForm, setBrandForm,
    existingBrands, setExistingBrands,
    loading, setLoading,
    error, setError,
    success, setSuccess,
    editingBrand, setEditingBrand,
    editDialogOpen, setEditDialogOpen,
    deleteDialogOpen, setDeleteDialogOpen,
    brandToDelete, setBrandToDelete,
    offerLoading, setOfferLoading,
    offerError, setOfferError,
    categories, setCategories,
    selectedCategory, setSelectedCategory,
    offerForm, setOfferForm,
    editingOffer, setEditingOffer,
    editOfferDialogOpen, setEditOfferDialogOpen,
    deleteOfferDialogOpen, setDeleteOfferDialogOpen,
    offerToDelete, setOfferToDelete,
    availableOffers, setAvailableOffers,
    availableOffersLoading, setAvailableOffersLoading,
    expandedBrands, setExpandedBrands,
    brandOffers, setBrandOffers,
    sharedElements, setSharedElements,
    sharedSelectedElement, setSharedSelectedElement,
    sharedSelectedElementContext, setSharedSelectedElementContext,
    sharedSelectedCategory, setSharedSelectedCategory,
    sharedSelectedSubtype, setSharedSelectedSubtype,
    sharedSelectedBrand, setSharedSelectedBrand,
    sharedSelectedOffer, setSharedSelectedOffer,
    sharedPages, setSharedPages,
    sharedSelectedTemplateId, setSharedSelectedTemplateId,
    fetchBrands, fetchCategories, fetchAvailableOffers,
    handleToggleBrandOffers, handleTabChange, handleOfferFormChange,
    handleCreateOffer, handleEditOffer, handleDeleteOfferClick, handleTogglePauseOffer,
    handleDeleteOfferConfirm, handleDeleteOfferCancel,
    handleEditOfferCancel, handleEditBrand, handleUpdateBrand,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    handleCreateBrand,
    isValidUrl,
  } = useBrandsContext();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Existing Brands</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setActiveTab(1)}>
          Add New Brand
        </Button>
      </Box>
    
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Brand</TableCell>
                <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Company</TableCell>
                <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Offers</TableCell>
                <TableCell align="right" sx={{ borderBottom: 2, borderColor: 'divider', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {existingBrands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography color="text.secondary">
                      No brands found. Create your first brand!
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                existingBrands.map((brand, index) => (
                  <React.Fragment key={brand._id}>
                    <TableRow
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <BusinessIcon />
                          </Avatar>
                          <Typography variant="body1" fontWeight="medium">
                            {brand.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="body2">
                          {brand.company || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {brand.contactInfo?.email ? (
                              <Link
                                href={`mailto:${brand.contactInfo.email}`}
                                color="primary"
                                underline="hover"
                              >
                                {brand.contactInfo.email}
                              </Link>
                            ) : '-'}
                          </Typography>
                          {brand.contactInfo?.phone && (
                            <Typography variant="caption" color="text.secondary">
                              {brand.contactInfo.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Button
                          size="small"
                          startIcon={
                            expandedBrands.has(brand._id) ?
                              <KeyboardArrowDownIcon /> :
                              <KeyboardArrowRightIcon />
                          }
                          onClick={() => handleToggleBrandOffers(brand._id)}
                          sx={{ textTransform: 'none' }}
                        >
                          {brandOffers[brand._id] ?
                            `${brandOffers[brand._id].length} offer${brandOffers[brand._id].length !== 1 ? 's' : ''}` :
                            'View offers'
                          }
                        </Button>
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <IconButton size="small" color="primary" onClick={() => handleEditBrand(brand)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(brand)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
    
                    {/* Expandable offers row */}
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          py: 0,
                          borderBottom: expandedBrands.has(brand._id) ? 1 : 0,
                          borderColor: 'divider'
                        }}
                      >
                        <Collapse in={expandedBrands.has(brand._id)} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                              Associated Offers
                            </Typography>
                            {brandOffers[brand._id] && brandOffers[brand._id].length > 0 ? (
                              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Offer Name</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Payout</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pays On</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Offer Countries</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Traffic Sources Allowed</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tracking Type</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Brand/Network</TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {brandOffers[brand._id].map((offer) => (
                                      <TableRow
                                        key={offer._id}
                                        sx={{
                                          '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                                          '&:hover': { backgroundColor: 'action.hover' }
                                        }}
                                      >
                                        <TableCell>
                                          <Typography variant="caption" fontWeight="bold">
                                            {offer.title}
                                          </Typography>
                                          {offer.published === false && (
                                            <Chip
                                              label="Paused"
                                              size="small"
                                              color="warning"
                                              sx={{ ml: 0.5, fontSize: '0.6rem', height: '16px' }}
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">{offer.payout || 'N/A'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">{offer.paysOn || 'N/A'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {offer.countriesAccepted && offer.countriesAccepted.length > 0 ? (
                                              offer.countriesAccepted.slice(0, 2).map((country, index) => (
                                                <Chip
                                                  key={index}
                                                  label={country}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ fontSize: '0.6rem', height: '16px' }}
                                                />
                                              ))
                                            ) : (
                                              <Typography variant="caption" color="text.secondary">N/A</Typography>
                                            )}
                                            {offer.countriesAccepted && offer.countriesAccepted.length > 2 && (
                                              <Chip
                                                label={`+${offer.countriesAccepted.length - 2}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.6rem', height: '16px' }}
                                              />
                                            )}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" sx={{ maxWidth: 120 }}>
                                            {offer.trafficSources ? offer.trafficSources.substring(0, 25) + '...' : 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={offer.trackingType || 'N/A'}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.6rem', height: '18px' }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption">
                                            {offer.brand?.networkBrand || brand.networkBrand || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => handleEditOffer(offer)}
                                              title="Edit Offer"
                                              sx={{ padding: '2px' }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              color={offer.published === false ? 'success' : 'warning'}
                                              onClick={() => handleTogglePauseOffer(offer)}
                                              title={offer.published === false ? 'Resume Offer' : 'Pause Offer'}
                                              sx={{ padding: '2px' }}
                                            >
                                              {offer.published === false
                                                ? <ResumeIcon fontSize="small" />
                                                : <PauseIcon fontSize="small" />}
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => handleDeleteOfferClick(offer)}
                                              title="Delete Offer"
                                              sx={{ padding: '2px' }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No offers associated with this brand yet.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BrandListTab;
